import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

export const ElinotifyTheme = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#fff1f3',
      100: '#ffd9e0',
      200: '#ffb3c1',
      300: '#ff8098',
      400: '#ff4d70',
      500: 'var(--notify-primary)',
      600: '#d10b2d',
      700: '#b00927',
      800: '#8f0720', // could be error
      900: '#700519',
      950: '#3d020d',
    },

    colorScheme: {
      light: {
        primary: {
          color: 'var(--notify-primary)',
          contrastColor: 'var(--notify-white)',
          hoverColor: '#d10b2d',
          activeColor: '#b00927',
        },

        highlight: {
          background: '#fff1f3',
          focusBackground: '#ffd9e0',
          color: 'var(--notify-primary)',
          focusColor: '#d10b2d',
        },

        surface: {
          0: 'var(--notify-white)',
          50: '#f8f9fb',
          100: '#f1f3f5',
          200: '#e3e7eb',
          300: '#d2d8df',
          400: '#aab4c0',
          500: '#7d8896',
          600: '#5f6975',
          700: '#47505b',
          800: 'var(--notify-dark)',
          900: '#1f252e',
          950: '#151922',
        },

        content: {
          background: 'var(--notify-white)',
          hoverBackground: '#f8f9fb',
          borderColor: '#dfe3e8',
          color: 'var(--notify-dark)',
          hoverColor: '#111827',
        },
      },
    },
  },

  components: {
    button: {
      root: {
        borderRadius: '4px',
        gap: '6px',
        paddingX: '16px',
        paddingY: '8px',
        sm: {
          paddingX: '12px',
          paddingY: '3.5px',
          fontSize: '12px',
        },
        label: {
          fontWeight: '600',
        },
        focusRing: {
          width: '1px',
          style: 'solid',
          offset: '0',
        },
        transitionDuration: '{transition.duration}',
        primary: {
          background: 'var(--notify-primary)',
          hoverBackground: 'var(--notify-primary-hover)',
          activeBackground: 'var(--notify-primary)',
          borderColor: 'var(--notify-primary)',
          hoverBorderColor: 'var(--notify-primary-hover)',
          activeBorderColor: 'var(--notify-primary)',
          color: 'var(--notify-white)',
          hoverColor: 'var(--notify-white)',
          activeColor: 'var(--notify-white)',
          focusRing: {
            color: 'var(--notify-focus)',
            shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
          },
        },
        secondary: {
          background: 'transparent',
          hoverBackground: 'var(--notify-surface-200)',
          activeBackground: 'transparent',
          borderColor: 'transparent',
          hoverBorderColor: 'var(--notify-surface-200)',
          activeBorderColor: 'transparent',
          color: 'var(--notify-surface-700)',
          hoverColor: 'var(--notify-dark)',
          activeColor: 'var(--notify-surface-700)',
          focusRing: {
            color: 'var(--notify-focus)',
            shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
          },
        },
        danger: {
          background: 'var(--notify-primary)',
          hoverBackground: 'var(--notify-primary-hover)',
          activeBackground: 'var(--notify-primary)',
          borderColor: 'var(--notify-primary)',
          hoverBorderColor: 'var(--notify-primary-hover)',
          activeBorderColor: 'var(--notify-primary)',
          color: 'var(--notify-white)',
          hoverColor: 'var(--notify-white)',
          activeColor: 'var(--notify-white)',
          focusRing: {
            color: 'var(--notify-focus)',
            shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
          },
        },
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              focusRing: {
                color: 'transparent',
                shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
              },
            },
            secondary: {
              focusRing: {
                color: 'transparent',
                shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
              },
            },
            danger: {
              focusRing: {
                color: 'transparent',
                shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
              },
            },
            contrast: {
              focusRing: {
                color: 'transparent',
                shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
              },
            },
          },
        },
      },
      outlined: {
        primary: {
          color: 'var(--notify-primary)',
          borderColor: 'var(--notify-primary)',
          hoverBackground: 'transparent',
          activeBackground: 'transparent',
        },
        secondary: {
          color: 'var(--notify-surface-700)',
          borderColor: 'var(--notify-surface-600)',
          hoverBackground: 'var(--notify-surface-200)',
          activeBackground: 'transparent',
        },
        danger: {
          color: 'var(--notify-primary)',
          borderColor: 'var(--notify-primary)',
          hoverBackground: 'transparent',
          activeBackground: 'transparent',
        },
      },
      text: {
        primary: {
          color: 'var(--notify-primary)',
          hoverBackground: 'var(--notify-surface-200)',
          activeBackground: 'transparent',
        },
        secondary: {
          color: 'var(--notify-surface-700)',
          hoverBackground: 'var(--notify-surface-200)',
          activeBackground: 'transparent',
        },
        danger: {
          color: 'var(--notify-primary)',
          hoverBackground: 'var(--notify-surface-200)',
          activeBackground: 'transparent',
        },
        plain: {
          color: 'var(--notify-surface-700)',
          hoverBackground: 'var(--notify-surface-200)',
          activeBackground: 'transparent',
        },
      },
      link: {
        color: 'var(--notify-primary)',
        hoverColor: 'var(--notify-primary-hover)',
        activeColor: 'var(--notify-primary)',
      },
    },

    inputtext: {
      colorScheme: {
        light: {
          root: {
            background: 'var(--notify-white)',
            borderColor: 'var(--notify-surface-600)',
            hoverBorderColor: 'var(--notify-surface-700)',
            focusBorderColor: 'var(--notify-focus)',
            focusRing: {
              shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
            },
            color: 'var(--notify-dark)',
            placeholderColor: 'color-mix(in srgb, var(--notify-dark) 55%, transparent)',
            borderRadius: '4px',
            invalidBorderColor: 'var(--notify-error)',
            invalidPlaceholderColor: 'var(--notify-error)',
            disabledColor: 'color-mix(in srgb, var(--notify-dark) 50%, transparent)',
            disabledBackground: 'var(--notify-surface-100)',
            paddingX: '7px',
            paddingY: '8px',
          },
        },
      },
    },

    textarea: {
      colorScheme: {
        light: {
          root: {
            background: 'var(--notify-white)',
            borderColor: 'var(--notify-surface-600)',
            hoverBorderColor: 'var(--notify-surface-700)',
            focusBorderColor: 'var(--notify-focus)',
            focusRing: {
              shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
            },
            color: 'var(--notify-dark)',
            placeholderColor: 'color-mix(in srgb, var(--notify-dark) 55%, transparent)',
            borderRadius: '4px',
            invalidBorderColor: 'var(--notify-error)',
            invalidPlaceholderColor: 'var(--notify-error)',
            disabledColor: 'color-mix(in srgb, var(--notify-dark) 50%, transparent)',
            disabledBackground: 'var(--notify-surface-100)',
            paddingX: '0.75rem',
            paddingY: '0.5rem',
          },
        },
      },
    },

    select: {
      colorScheme: {
        light: {
          root: {
            background: 'var(--notify-white)',
            borderColor: 'var(--notify-surface-600)',
            hoverBorderColor: 'var(--notify-surface-700)',
            focusBorderColor: 'var(--notify-focus)',
            focusRing: {
              shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
            },
            color: 'var(--notify-dark)',
            placeholderColor: 'color-mix(in srgb, var(--notify-dark) 55%, transparent)',
            borderRadius: '4px',
            invalidBorderColor: 'var(--notify-error)',
            disabledColor: 'color-mix(in srgb, var(--notify-dark) 50%, transparent)',
            disabledBackground: 'var(--notify-surface-100)',
            paddingX: '8px',
            paddingY: '6.5px',
          },
          overlay: {
            background: '{surface.0}',
            color: '{surface.800}',
            borderColor: 'none',
            shadow:
              '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
            borderRadius: '4px',
          },
          // could be overwritten by listbox>option rules because listbox is being used inside multiselect
          option: {
            color: '{surface.800}',
            selectedColor: '{surface.900}',
            selectedBackground: '{surface.200}',
            selectedFocusColor: '{surface.900}',
            selectedFocusBackground: '{surface.200}',
            focusColor: '{surface.700}',
            focusBackground: '{surface.100}',
          },
          dropdown: {
            color: 'var(--notify-primary)',
          },
        },
      },
    },

    multiselect: {
      colorScheme: {
        light: {
          root: {
            background: 'var(--notify-white)',
            borderColor: 'var(--notify-surface-600)',
            hoverBorderColor: 'var(--notify-surface-700)',
            focusBorderColor: 'var(--notify-focus)',
            focusRing: {
              shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
            },
            color: 'var(--notify-dark)',
            placeholderColor: 'color-mix(in srgb, var(--notify-dark) 55%, transparent)',
            borderRadius: '4px',
            disabledColor: 'color-mix(in srgb, var(--notify-dark) 50%, transparent)',
            disabledBackground: 'var(--notify-surface-100)',
            invalidBorderColor: 'var(--notify-error)',
            paddingX: '0.75rem',
            paddingY: '0.5rem',
          },
          overlay: {
            background: '{surface.0}',
            color: '{surface.800}',
            borderColor: 'none',
            shadow:
              '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
            borderRadius: '4px',
          },
          // could be overwritten by listbox>option rules because listbox is being used inside multiselect
          option: {
            color: '{surface.800}',
            selectedColor: '{surface.900}',
            selectedBackground: '{surface.200}',
            selectedFocusColor: '{surface.900}',
            selectedFocusBackground: '{surface.200}',
            focusColor: '{surface.700}',
            focusBackground: '{surface.100}',
          },
          dropdown: {
            color: 'var(--notify-primary)',
          },
        },
      },
    },

    checkbox: {
      colorScheme: {
        light: {
          root: {
            background: 'var(--notify-white)',

            borderColor: 'var(--notify-surface-600)',
            hoverBorderColor: 'var(--notify-surface-700)',
            focusBorderColor: 'var(--notify-focus)',
            focusRing: {
              color: 'transparent',
              shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
            },

            checkedBackground: 'var(--notify-primary)',
            checkedHoverBackground: 'var(--notify-primary-hover)',
            checkedBorderColor: 'var(--notify-primary)',
            checkedHoverBorderColor: 'var(--notify-primary-hover)',
            checkedFocusBorderColor: 'var(--notify-primary)',
            checkedDisabledBorderColor: 'color-mix(in srgb, var(--notify-primary) 35%, transparent)',
            disabledBackground: 'var(--notify-surface-100)',

            invalidBorderColor: 'var(--notify-error)',

            width: '20px',
            height: '20px',
            borderRadius: '4px',
          },
          icon: {
            checkedColor: 'var(--notify-white)',
            checkedHoverColor: 'var(--notify-white)',
            disabledColor: 'color-mix(in srgb, var(--notify-white) 35%, transparent)',
          },
        },
      },
    },

    radiobutton: {
      colorScheme: {
        light: {
          root: {
            filledBackground: 'var(--notify-white)',
            background: 'var(--notify-white)',
            borderColor: 'var(--notify-surface-600)',
            hoverBorderColor: 'var(--notify-surface-700)',
            focusBorderColor: 'var(--notify-focus)',
            focusRing: {
              color: 'var(--notify-focus)',
              shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
            },
            checkedBackground: 'var(--notify-white)',
            checkedHoverBackground: 'var(--notify-white)',
            checkedBorderColor: 'var(--notify-surface-600)',
            checkedHoverBorderColor: 'var(--notify-surface-700)',
            checkedFocusBorderColor: 'var(--notify-surface-600)',
            checkedDisabledBorderColor: 'var(--notify-surface-600)',
            disabledBackground: 'var(--notify-surface-100)',
            invalidBorderColor: '{primary.800}',
            width: '20px',
            height: '20px',
          },
          icon: {
            size: '10px',
            checkedColor: 'var(--notify-primary)',
            checkedHoverColor: 'var(--notify-primary-hover)',
            disabledColor: 'color-mix(in srgb, var(--notify-primary) 35%, transparent)',
          },
        },
      },
    },

    chip: {
      root: {
        borderRadius: '4px',
        paddingX: '8px',
        paddingY: '5px',
        gap: '6px',
      },
      icon: {
        size: '0.875rem',
      },
      removeIcon: {
        size: '14px',
        focusRing: {
          color: 'var(--notify-focus)',
          shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
        },
      },
      colorScheme: {
        light: {
          root: {
            background: 'var(--notify-surface-300)',
            color: 'var(--notify-dark)',
          },
          icon: {
            color: 'var(--notify-dark)',
          },
          removeIcon: {
            color: 'var(--notify-dark)',
          },
        },
      },
    },

    card: {
      colorScheme: {
        light: {
          root: {
            background: 'var(--notify-white)',
            color: 'var(--notify-dark)',
            shadow: '#e3e7eb',
            borderRadius: '16px',
          },
        },
      },
    },

    paginator: {
      colorScheme: {
        light: {
          root: {
            background: 'var(--notify-white)',
            color: 'var(--notify-surface-700)',
          },
          navButton: {
            color: 'var(--notify-surface-700)',
            selectedColor: 'var(--notify-primary)',
            selectedBackground: 'var(--notify-surface-100)',
            hoverColor: 'var(--notify-primary)',
            hoverBackground: 'var(--notify-surface-100)',
            borderRadius: '3px',
            focusRing: {
              color: 'var(--notify-focus)',
              shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
            },
            height: '2.357rem',
            width: '2.357rem',
          },
        },
      },
    },

    datatable: {
      colorScheme: {
        light: {
          header: {
            color: 'var(--notify-surface-700)',
            background: 'var(--notify-white)',
          },
          headerCell: {
            color: 'var(--notify-surface-700)',
            background: 'var(--notify-white)',
            borderColor: 'var(--notify-surface-600)',
            selectedColor: 'var(--notify-surface-700)',
            selectedBackground: 'var(--notify-white)',
            padding: '10px',
            hoverColor: 'var(--notify-dark)',
            hoverBackground: 'var(--notify-white)',
            focusRing: {
              color: 'transparent',
            },
          },
          sortIcon: {
            color: 'var(--notify-surface-700)',
            hoverColor: 'var(--notify-dark)',
          },
          row: {
            color: 'var(--notify-dark)',
            background: 'var(--notify-white)',
            stripedBackground: 'var(--notify-surface-50)',
            hoverColor: 'var(--notify-dark)',
            hoverBackground: 'var(--notify-white)',
            focusRing: {
              color: 'var(--notify-focus)',
              shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
              width: '1px',
              style: 'solid',
              offset: '-1px',
            },
          },
          bodyCell: {
            padding: '15px 10px',
          },
          filter: {
            overlayPopover: {
              background: 'var(--notify-white)',
              color: 'var(--notify-dark)',
              borderColor: 'transparent',
              borderRadius: '4px',
              shadow:
                '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
            },
            rule: {
              borderColor: 'var(--notify-surface-600)',
            },
          },
        },
      },
    },

    popover: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            borderColor: 'none',
            borderRadius: '4px',
            shadow:
              '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
            arrowOffset: '10px',
            color: '{surface.800}',
          },
          content: {
            padding: '10px',
          },
        },
      },
    },

    autocomplete: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            borderColor: 'none',
            borderRadius: '4px',
            shadow:
              '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
            color: '{surface.800}',
          },
          overlay: {
            background: '{surface.0}',
            color: '{surface.800}',
            borderColor: 'none',
            shadow:
              '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
          },
          option: {
            color: '{surface.800}',
            selectedColor: '{surface.900}',
            selectedBackground: '{surface.200}',
            selectedFocusColor: '{surface.900}',
            selectedFocusBackground: '{surface.200}',
            focusColor: '{surface.700}',
            focusBackground: '{surface.100}',
          },
          dropdown: {
            background: '{surface.0}',
            color: '{surface.800}',
          },
        },
      },
    },

    listbox: {
      colorScheme: {
        light: {
          root: {
            color: '{surface.800}',
            background: '{surface.0}',
            borderColor: 'none',
            borderRadius: '4px',
            shadow: 'none',
          },
          option: {
            color: '{surface.800}',
            selectedColor: '{surface.900}',
            selectedBackground: '{surface.200}',
            selectedFocusColor: '{surface.900}',
            selectedFocusBackground: '{surface.200}',
            focusColor: '{surface.700}',
            focusBackground: '{surface.100}',
          },
          list: {
            padding: '0 2px 0 0',
          },
        },
      },
    },

    menu: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            borderColor: 'none',
            borderRadius: '4px',
            shadow:
              '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
          },
          list: {
            padding: '5px 0',
          },
          item: {
            color: '{surface.800}',
            focusColor: '{surface.800}',
            focusBackground: '{surface.100}',
            padding: '12px',
            icon: {
              color: '{surface.800}',
              focusColor: '{surface.800}',
            },
          },
        },
      },
    },

    tabs: {
      colorScheme: {
        light: {
          tablist: {
            background: 'var(--notify-white)',
            borderColor: 'var(--notify-surface-600)',
            borderWidth: '0 0 2px 0',
          },
          tabpanel: {
            background: 'var(--notify-white)',
            padding: '5px 0 0 0',
          },
          tab: {
            color: 'var(--notify-dark)',
            background: 'var(--notify-white)',
            activeColor: 'var(--notify-dark)',
            activeBorderColor: 'var(--notify-primary)',
            borderWidth: '0 0 2px 0',
            borderColor: 'var(--notify-surface-600)',
            hoverBorderColor: '{surface.500}',
            hoverColor: 'var(--notify-dark)',
            padding: '15px 0',
            margin: '0 25px -2px 0',
            focusRing: {
              color: 'transparent',
            },
          },
        },
      },
      activeBar: {
        background: 'var(--notify-primary)',
      },
    },

    dialog: {
      colorScheme: {
        light: {
          root: {
            background: 'var(--notify-white)',
            borderColor: 'transparent',
            color: 'var(--notify-dark)',
          },
          content: {
            padding: 'unset',
          },
        },
      },
    },

    tooltip: {
      root: {
        background: 'color-mix(in srgb, var(--notify-dark) 80%, transparent)',
        color: 'var(--notify-white)',
        padding: '2px 8px 3px 8px',
        borderRadius: '4px',
      },
    },

    datepicker: {
      colorScheme: {
        light: {
          dropdown: {
            background: '{surface.100}',
            hoverBackground: '{surface.200}',
            activeBackground: '{surface.300}',
            color: '{surface.600}',
            hoverColor: '{surface.700}',
            activeColor: '{surface.800}',
          },
          today: {
            background: 'var(--notify-white)',
            color: 'var(--notify-dark)',
          },
        },
      },
      root: {
        transitionDuration: '{transition.duration}',
      },
      panel: {
        background: 'var(--notify-white)',
        borderColor: 'none',
        color: 'var(--notify-dark)',
        borderRadius: '4px',
        shadow: '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
        padding: '0.75rem',
      },
      header: {
        background: 'var(--notify-white)',
        borderColor: 'var(--notify-surface-600)',
        color: 'var(--notify-dark)',
        padding: '0 0 0.5rem 0',
      },
      title: {
        gap: '0.5rem',
        fontWeight: '500',
      },
      dropdown: {
        width: '2.5rem',
        sm: {
          width: '2rem',
        },
        lg: {
          width: '3rem',
        },
        borderColor: '{surface.300}',
        hoverBorderColor: '{surface.400}',
        activeBorderColor: '{surface.400}',
        borderRadius: '4px',
        focusRing: {
          color: 'var(--notify-focus)',
          shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
          width: '1px',
          style: 'solid',
          offset: '0',
        },
      },
      inputIcon: {
        color: '{surface.300}',
      },
      selectMonth: {
        hoverBackground: '{surface.100}',
        color: 'var(--notify-dark)',
        hoverColor: '{surface.900}',
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
      },
      selectYear: {
        hoverBackground: '{surface.100}',
        color: 'var(--notify-dark)',
        hoverColor: '{surface.900}',
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
      },
      group: {
        borderColor: 'var(--notify-surface-600)',
        gap: '0.75rem',
      },
      dayView: {
        margin: '0.5rem 0 0 0',
      },
      weekDay: {
        padding: '0.25rem',
        fontWeight: '500',
        color: 'var(--notify-surface-700)',
      },
      date: {
        hoverBackground: 'var(--notify-surface-100)',
        selectedBackground: 'var(--notify-primary)',
        rangeSelectedBackground: 'color-mix(in srgb, var(--notify-primary) 10%, transparent)',
        color: 'var(--notify-dark)',
        hoverColor: 'var(--notify-primary)',
        selectedColor: 'var(--notify-white)',
        rangeSelectedColor: 'var(--notify-dark)',
        width: '2rem',
        height: '2rem',
        borderRadius: '50%',
        padding: '0.25rem',
        focusRing: {
          color: 'var(--notify-focus)',
          shadow: '0 0 1px 2px color-mix(in srgb, var(--notify-focus) 15%, transparent)',
          width: '1px',
          style: 'solid',
          offset: '0',
        },
      },
      monthView: {
        margin: '0.5rem 0 0 0',
      },
      month: {
        padding: '0.375rem',
        borderRadius: '4px',
      },
      yearView: {
        margin: '0.5rem 0 0 0',
      },
      year: {
        padding: '0.375rem',
        borderRadius: '4px',
      },
      buttonbar: {
        padding: '0.5rem 0 0 0',
        borderColor: '{surface.200}',
      },
      timePicker: {
        padding: '0.5rem 0 0 0',
        borderColor: '{surface.200}',
        gap: '0.5rem',
        buttonGap: '0.25rem',
      },
      today: {
        background: 'var(--notify-white)',
        color: 'var(--notify-dark)',
      },
    },
  },
});
